import { extent, max, min } from 'd3-array'
import type { ScaleLinear, ScaleTime } from 'd3-scale'
import { scaleLinear, scaleTime } from 'd3-scale'
import type { ReactNode } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'

import { X_AXIS_HEIGHT, Y_AXIS_PADDING, Y_AXIS_WIDTH } from '@/features/bidding/components/cascading/charts/constants'
import type { AreaGraphElement, AreaGraphProps } from '@/features/bidding/components/cascading/charts/types'
import useResizeObserver from '@/hooks/useResizeObserver'

import AreaGraphLayers from './AreaGraphLayers'
import EstimationMarkers, { EstimationPatternDef } from './EstimationMarkers'
import GraphBackground from './GraphBackground'
import GraphHoverLine from './GraphHoverLine'
import GridLines from './GridLines'
import OverlayLines from './OverlayLines'
import TimeAxis from './TimeAxis'
import ValueAxis from './ValueAxis'

/** Small left padding for chart content */
const LEFT_PADDING = 5

/**
 * Main AreaGraph component - orchestrates all chart elements.
 * Y-axis is positioned on the right side.
 */
type BarLabelConfig = {
  /** Format the value for display */
  formatValue: (value: number) => string
  /** Minimum bar height in pixels to show label */
  minBarHeight?: number
  /** Minimum bar width in pixels to show label */
  minBarWidth?: number
}

function AreaGraph({
  data,
  testId,
  layerKeys,
  layerFill,
  layerStroke,
  height,
  datetimes,
  formatTick,
  estimated,
  onHover,
  overlayLines,
  markerAtZero,
  barPadding = 0,
  barLabels,
  showGridLines = false,
  onClick,
  renderOverlay,
}: Readonly<
  Omit<AreaGraphProps, 'valueAxisLabel'> & {
    barPadding?: number
    barLabels?: BarLabelConfig
    showGridLines?: boolean
    onClick?: (hourIndex: number, relativeY: number) => void
    renderOverlay?: (ctx: {
      timeScale: ScaleTime<number, number>
      valueScale: ScaleLinear<number, number>
    }) => ReactNode
  }
>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { width: observerWidth } = useResizeObserver(containerRef)

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  // Calculate dimensions - reserve space for Y-axis on the right
  const containerWidth = Math.max(observerWidth - Y_AXIS_WIDTH - LEFT_PADDING, 0)
  const containerHeight = Number.parseInt(height, 10) - X_AXIS_HEIGHT

  // Create time scale - extend domain to include the last bar's end time
  const timeScale = useMemo(() => {
    const [minDate, maxDate] = extent(datetimes)
    if (!minDate || !maxDate) {
      return scaleTime().domain([new Date(), new Date()]).range([0, containerWidth])
    }

    // Calculate interval to extend domain for the last bar
    let endTime = maxDate
    if (datetimes.length >= 2) {
      const interval = datetimes[1].getTime() - datetimes[0].getTime()
      endTime = new Date(maxDate.getTime() + interval)
    }

    return scaleTime().domain([minDate, endTime]).range([0, containerWidth])
  }, [datetimes, containerWidth])

  // Create value scale - handle both positive-only and diverging (positive/negative) data
  const { valueScale, isDiverging } = useMemo(() => {
    // For stacked areas, we need to sum up all positive and negative values per data point
    // to get the actual stacked height
    const stackedTotals = data.map((d) => {
      let positiveSum = 0
      let negativeSum = 0

      for (const key of layerKeys) {
        const value = d.layerData[key]
        if (Number.isFinite(value)) {
          if (value >= 0) {
            positiveSum += value
          } else {
            negativeSum += value
          }
        }
      }

      return { positiveSum, negativeSum }
    })

    const maxPositive = max(stackedTotals, (d) => d.positiveSum) ?? 0
    const minNegative = min(stackedTotals, (d) => d.negativeSum) ?? 0

    // Get values from overlay lines if present (these don't stack)
    const overlayValues = overlayLines
      ? data.flatMap((d) => overlayLines.map((line) => line.getValue(d)).filter(Number.isFinite))
      : []

    const overlayMax = max(overlayValues) ?? 0
    const overlayMin = min(overlayValues) ?? 0

    const maxValue = Math.max(maxPositive, overlayMax)
    const minValue = Math.min(minNegative, overlayMin)

    // For diverging data, make the scale symmetric around zero
    if (minValue < 0) {
      const absMax = Math.max(Math.abs(minValue), Math.abs(maxValue), 1)
      const padding = absMax * 0.1

      return {
        valueScale: scaleLinear()
          .domain([-(absMax + padding), absMax + padding])
          .range([containerHeight, Y_AXIS_PADDING]),
        isDiverging: true,
      }
    }

    // For positive-only data, start at 0
    const padding = maxValue * 0.1

    return {
      valueScale: scaleLinear()
        .domain([0, maxValue + padding])
        .range([containerHeight, Y_AXIS_PADDING]),
      isDiverging: false,
    }
  }, [data, layerKeys, overlayLines, containerHeight])

  // Hover handlers
  const handleMouseMove = useCallback(
    (index: number) => {
      setHoveredIndex(index)

      const dataPoint = data[index] ?? null
      if (!dataPoint || !containerRef.current) {
        onHover?.(null, null)
        return
      }

      // Calculate marker position (same logic as GraphHoverLine)
      const currentDatetime = datetimes[index]
      const nextDatetime = datetimes[index + 1]

      let markerX = timeScale(currentDatetime)

      // Center the marker on the bar by adding half the interval
      if (nextDatetime) {
        const interval = timeScale(nextDatetime) - timeScale(currentDatetime)
        markerX += interval * 0.5
      } else if (datetimes.length >= 2) {
        const estimatedInterval = timeScale(datetimes[1]) - timeScale(datetimes[0])
        markerX += estimatedInterval * 0.5
      }

      // Get the value for the marker position
      const firstLayerKey = layerKeys[0]
      const value = firstLayerKey ? dataPoint.layerData[firstLayerKey] : 0
      const markerY = valueScale(value)

      // Convert to viewport coordinates
      const rect = containerRef.current.getBoundingClientRect()
      const viewportX = rect.left + LEFT_PADDING + markerX
      const viewportY = rect.top + markerY

      onHover?.({ x: viewportX, y: viewportY }, dataPoint)
    },
    [data, datetimes, layerKeys, onHover, timeScale, valueScale],
  )

  const handleMouseOut = useCallback(() => {
    setHoveredIndex(null)
    onHover?.(null, null)
  }, [onHover])

  const hoveredDataPoint = hoveredIndex !== null ? (data[hoveredIndex] as AreaGraphElement) : null

  // Calculate marker fill color based on hovered data point value
  const hoveredMarkerFill = useMemo(() => {
    if (!hoveredDataPoint || layerKeys.length === 0) return undefined
    const firstLayerKey = layerKeys[0]
    // layerFill is curried: layerFill(key) returns (d) => color
    return layerFill(firstLayerKey)({ data: hoveredDataPoint })
  }, [hoveredDataPoint, layerKeys, layerFill])

  if (containerWidth <= 0) {
    return <div ref={containerRef} data-testid={testId} style={{ width: '100%', height }} />
  }

  // Check if we should render estimation markers
  const hasEstimation = estimated && estimated.some(Boolean)

  return (
    <div ref={containerRef} data-testid={testId} style={{ width: '100%' }}>
      <svg height={height} style={{ overflow: 'visible', width: '100%' }}>
        {/* Pattern definitions */}
        {hasEstimation && (
          <defs>
            <EstimationPatternDef />
          </defs>
        )}
        <g transform={`translate(${LEFT_PADDING}, 0)`}>
          {/* Grid lines (background) */}
          {showGridLines && <GridLines scale={valueScale} width={containerWidth} />}

          {/* Stacked area layers */}
          <AreaGraphLayers
            data={data}
            hoveredIndex={hoveredIndex}
            id={testId}
            layerFill={layerFill}
            layerKeys={layerKeys}
            layerStroke={layerStroke}
            timeScale={timeScale}
            valueScale={valueScale}
          />

          {/* Bar padding - white rectangles at bar boundaries */}
          {barPadding > 0 &&
            datetimes.map((dt, i) => {
              const barX = timeScale(dt)
              const nextDt =
                datetimes[i + 1] ?? new Date(dt.getTime() + (datetimes[1]?.getTime() - datetimes[0]?.getTime()))
              const barWidth = timeScale(nextDt) - barX
              const paddingWidth = barWidth * barPadding
              const [y2, y1] = valueScale.range()
              return (
                <g key={`padding-${dt.getTime()}`}>
                  {/* Left padding */}
                  <rect fill="white" height={y2 - y1} width={paddingWidth / 2} x={barX} y={y1} />
                  {/* Right padding */}
                  <rect
                    fill="white"
                    height={y2 - y1}
                    width={paddingWidth / 2}
                    x={barX + barWidth - paddingWidth / 2}
                    y={y1}
                  />
                </g>
              )
            })}

          {/* Subtle vertical separators between bars (when no padding) */}
          {barPadding === 0 &&
            layerStroke &&
            datetimes.map((dt, i) => {
              if (i === 0) return null
              const x = timeScale(dt)
              return (
                <line
                  key={dt.getTime()}
                  stroke="white"
                  strokeOpacity={0.6}
                  strokeWidth={0.5}
                  x1={x}
                  x2={x}
                  y1={Y_AXIS_PADDING}
                  y2={containerHeight}
                />
              )
            })}

          {/* Bar labels showing MW values */}
          {barLabels &&
            datetimes.map((dt, i) => {
              const dataPoint = data[i]
              if (!dataPoint) return null

              const barX = timeScale(dt)
              const nextDt =
                datetimes[i + 1] ?? new Date(dt.getTime() + (datetimes[1]?.getTime() - datetimes[0]?.getTime()))
              const barWidth = timeScale(nextDt) - barX
              const effectiveBarWidth = barWidth * (1 - barPadding)
              const minBarWidth = barLabels.minBarWidth ?? 20
              const minBarHeight = barLabels.minBarHeight ?? 14

              // Don't show labels if bar is too narrow
              if (effectiveBarWidth < minBarWidth) return null

              // Calculate bar segments for each layer
              const segments: Array<{ key: string; y: number; height: number; value: number }> = []
              let positiveStackY = valueScale(0)
              let negativeStackY = valueScale(0)

              for (const key of layerKeys) {
                const value = dataPoint.layerData[key] ?? 0
                if (value === 0) continue

                const valueY = valueScale(Math.max(value, 0))
                const zeroY = valueScale(0)

                if (value > 0) {
                  const segmentHeight = zeroY - valueY
                  const y = positiveStackY - segmentHeight
                  segments.push({ key, y, height: segmentHeight, value })
                  positiveStackY = y
                } else {
                  const segmentHeight = valueScale(value) - zeroY
                  const y = negativeStackY
                  segments.push({ key, y, height: segmentHeight, value: Math.abs(value) })
                  negativeStackY = y + segmentHeight
                }
              }

              return (
                <g key={`labels-${dt.getTime()}`}>
                  {segments.map((seg) => {
                    // Only show label if segment is tall enough
                    if (seg.height < minBarHeight) return null
                    const centerX = barX + barWidth / 2
                    const centerY = seg.y + seg.height / 2
                    return (
                      <text
                        key={seg.key}
                        dominantBaseline="central"
                        fill="white"
                        fontSize="9"
                        fontWeight="500"
                        style={{ pointerEvents: 'none' }}
                        textAnchor="middle"
                        x={centerX}
                        y={centerY}
                      >
                        {barLabels.formatValue(seg.value)}
                      </text>
                    )
                  })}
                </g>
              )
            })}

          {/* White gap around zero for diverging data */}
          {isDiverging && <rect fill="white" height={4} width={containerWidth} x={0} y={valueScale(0) - 2} />}

          {/* Zero line for diverging charts */}
          {isDiverging && showGridLines && (
            <line stroke="#9ca3af" strokeWidth={1} x1={0} x2={containerWidth} y1={valueScale(0)} y2={valueScale(0)} />
          )}

          {/* Overlay lines */}
          {(overlayLines?.length ?? 0) > 0 && (
            <OverlayLines data={data} lines={overlayLines!} timeScale={timeScale} valueScale={valueScale} />
          )}

          {/* Estimation markers overlay */}
          {hasEstimation && (
            <EstimationMarkers
              datetimes={datetimes}
              estimated={estimated}
              height={containerHeight}
              timeScale={timeScale}
            />
          )}

          {/* Time axis */}
          <TimeAxis datetimes={datetimes} height={containerHeight} scale={timeScale} width={containerWidth} />

          {/* Value axis - positioned on the right */}
          <ValueAxis formatTick={formatTick} scale={valueScale} width={containerWidth} />

          {renderOverlay?.({ timeScale, valueScale })}

          {/* Interaction layer - rendered on top of bars to capture mouse events */}
          <GraphBackground
            datetimes={datetimes}
            height={containerHeight}
            timeScale={timeScale}
            width={containerWidth}
            onClick={onClick ? (_index, _x, y) => onClick(_index, y) : undefined}
            onMouseMove={handleMouseMove}
            onMouseOut={handleMouseOut}
          />

          {/* Hover line - rendered last to be on top */}
          <GraphHoverLine
            dataPoint={hoveredDataPoint}
            datetimes={datetimes}
            height={containerHeight}
            layerKeys={layerKeys}
            markerAtZero={markerAtZero}
            markerFill={hoveredMarkerFill}
            selectedTimeIndex={hoveredIndex}
            timeScale={timeScale}
            valueScale={valueScale}
          />
        </g>
      </svg>
    </div>
  )
}

export default AreaGraph
